import React from 'react';
import { Element, Text as HastText } from 'hast';
import { Text as ChakraText, Link as ChakraLink } from '@chakra-ui/react';

// Define a type union for possible node children that we handle
type HastNode = Element | HastText;

/** Interface for caller-provided rendering functions */
export interface NodeRenderers {
    text(text: string, key: string): React.ReactNode;
    strong(children: React.ReactNode[], key: string): React.ReactNode;
    emphasis(children: React.ReactNode[], key: string): React.ReactNode;
    link(children: React.ReactNode[], href: string, key: string): React.ReactNode;
    // Add ul renderer to support nested lists
    ul?(children: React.ReactNode[], key: string): React.ReactNode;
    // Add li renderer to support nested lists
    li?(children: React.ReactNode[], key: string): React.ReactNode;
    // Optional: renderParagraph(children: React.ReactNode[], key: string): React.ReactNode;
    // Optional: renderUnhandledElement(node: Element, key: string): React.ReactNode;
}

// Internal helper to render a single node using provided renderers
function renderNode(
    node: HastNode,
    index: number,
    parentContext: string,
    renderers: NodeRenderers // Added renderers parameter
): React.ReactNode[] {
    const key = `${parentContext}-${index}-${node.type}`;

    // 1. Handle direct text nodes
    if (node.type === 'text') {
        // Use the provided text renderer
        return [renderers.text(node.value, key)];
    }

    // 2. Handle element nodes
    if (node.type === 'element') {
        const children = node.children as HastNode[] | undefined;
        // Recursive call passes renderers down
        const renderedChildren = extractAndRenderNodes(
            children,
            `${parentContext} > ${node.tagName}`,
            renderers // Pass renderers down
        );

        switch (node.tagName) {
            case 'p':
                // Wrap the paragraph's children in a Fragment to keep them grouped
                // This might help the parent renderer display the content correctly.
                return [<React.Fragment key={key}>{renderedChildren}</React.Fragment>];
            case 'strong':
                // Use the provided strong renderer
                return [renderers.strong(renderedChildren, key)];
            case 'em':
                // Use the provided emphasis renderer
                return [renderers.emphasis(renderedChildren, key)];
            case 'a':
                if (node.properties?.href) {
                    // Use the provided link renderer
                    return [
                        renderers.link(
                            renderedChildren,
                            String(node.properties.href),
                            key
                        ),
                    ];
                } else {
                    // Fallback: render children without link if href is missing
                    console.warn(
                        `${parentContext}: <a> tag found without href property.`
                    );
                    return renderedChildren;
                }
            // Add proper handling for ul elements
            case 'ul':
                // If there's a custom ul renderer, use it
                if (renderers.ul) {
                    return [renderers.ul(renderedChildren, key)];
                }
                // Otherwise, wrap children in Fragment to maintain structure
                return [<React.Fragment key={key}>{renderedChildren}</React.Fragment>];
            // Add proper handling for li elements
            case 'li':
                // If there's a custom li renderer, use it
                if (renderers.li) {
                    return [renderers.li(renderedChildren, key)];
                }
                // Otherwise, wrap children in Fragment to maintain structure
                return [<React.Fragment key={key}>{renderedChildren}</React.Fragment>];
            // Add support for br elements
            case 'br':
                // Line breaks don't have children, just return a React fragment with a <br> element
                return [
                    <React.Fragment key={key}>
                        <br />
                    </React.Fragment>,
                ];
            default:
                // Instead of throwing an error for unhandled elements, log it and continue
                const nodeDescription = `<${node.tagName}>`;
                console.error(
                    `${parentContext}: Skipping unhandled element type '${nodeDescription}'. Consider adding support for this element type.`
                );

                // Try to render children if they exist, otherwise return empty array
                // This allows the rendering to continue with the parts we understand
                if (renderedChildren && renderedChildren.length > 0) {
                    console.error(
                        `${parentContext}: Attempting to render children of unhandled element '${nodeDescription}'`
                    );
                    return renderedChildren;
                }
                return [];
        }
    }

    // Fallback for unexpected node types (shouldn't be reached)
    console.error('Unreachable code reached in renderNode. Node:', node);
    return [];
}

/**
 * Recursively processes nested hast nodes using caller-provided renderers.
 *
 * @param nodes The array of child nodes (hast elements/text) to process.
 * @param parentContext A string describing the parent element (e.g., 'blockquote', 'li') for error messages.
 * @param renderers An object containing functions to render specific node types.
 * @returns An array of React nodes.
 */
export function extractAndRenderNodes(
    nodes: HastNode[] | undefined,
    parentContext: string,
    renderers: NodeRenderers // Added renderers parameter
): React.ReactNode[] {
    if (!nodes) {
        return [];
    }

    return nodes.flatMap((node, index) =>
        renderNode(node, index, parentContext, renderers)
    );
}
